import { useState, useCallback } from 'react'
import {
  useTracking,
  useRemoveTrackingMutation,
  useUpdateTrackingMutation,
  useTriggerAlertsMutation,
  useUrlFilters,
  usePageTabs,
} from '../hooks'
import { useViewSort } from '../context/ViewSortContext'
import { useToast } from '../context/ToastContext'
import { ViewSortControls } from '../components/common/ViewSortControls'
import { Pagination } from '../components/common/Pagination'
import { LoadingState } from '../components/common/Spinner'
import { Button, IconButton, SearchInput, PageTabBar } from '../components/common'
import { formatCurrency, formatDate, cn, getCaretSortIcon } from '../utils'
import type { TrackingFilters, TrackedRelease } from '../types'
import styles from './PageStyles.module.css'

const DEFAULT_TRACKING_FILTERS: TrackingFilters = { page: 1, per_page: 50, sort: 'added_at', order: 'desc' }

function parseTrackingFilters(searchParams: URLSearchParams): TrackingFilters {
  return {
    page: Number(searchParams.get('page')) || 1,
    per_page: Number(searchParams.get('per_page')) || 50,
    sort: searchParams.get('sort') || 'added_at',
    order: (searchParams.get('order') as 'asc' | 'desc') || 'desc',
    q: searchParams.get('q') || undefined,
  }
}

export function TrackingPage() {
  const { viewMode } = useViewSort()
  const { filters, updateFilters, replaceFilters } = useUrlFilters({ parse: parseTrackingFilters })
  const { showToast } = useToast()

  const { data, isLoading, error } = useTracking(filters)
  const removeTrackingMutation = useRemoveTrackingMutation()
  const updateTrackingMutation = useUpdateTrackingMutation()
  const triggerAlertsMutation = useTriggerAlertsMutation()

  const [searchInput, setSearchInput] = useState(filters.q || '')

  const setFiltersFromTab = useCallback((f: TrackingFilters) => {
    replaceFilters(f)
    setSearchInput(f.q ?? '')
  }, [replaceFilters])

  const { savedSearches: tabs, activeTabId, handleTabClick, handleCreateTab, handleDeleteTab } = usePageTabs<TrackingFilters>({
    scope: 'tracking',
    filters,
    setFilters: setFiltersFromTab,
    defaultFilters: DEFAULT_TRACKING_FILTERS,
  })
  const [selectedItem, setSelectedItem] = useState<TrackedRelease | null>(null)

  // Inline edit state for detail panel
  const [editingTarget, setEditingTarget] = useState(false)
  const [editTarget, setEditTarget] = useState('')
  const [editingNotes, setEditingNotes] = useState(false)
  const [editNotes, setEditNotes] = useState('')



  const handleSort = (column: string) => {
    if (filters.sort === column) {
      updateFilters({ order: filters.order === 'desc' ? 'asc' : 'desc' })
    } else {
      updateFilters({ sort: column, order: 'desc' })
    }
  }

  const handleUntrack = async (releaseId: number) => {
    if (confirm('Remove this release from tracking?')) {
      await removeTrackingMutation.mutateAsync(releaseId)
      setSelectedItem(null)
    }
  }

  const handleSelect = (item: TrackedRelease) => {
    setSelectedItem(item)
    setEditingTarget(false)
    setEditingNotes(false)
  }

  const handleSaveTarget = async () => {
    if (!selectedItem) return
    const price = parseFloat(editTarget)
    await updateTrackingMutation.mutateAsync({
      releaseId: selectedItem.release_id,
      data: { target_buy_price: isNaN(price) ? undefined : price },
    })
    setSelectedItem({ ...selectedItem, target_buy_price: isNaN(price) ? null : price })
    setEditingTarget(false)
    showToast('Target price updated', 'success')
  }

  const handleSaveNotes = async () => {
    if (!selectedItem) return
    await updateTrackingMutation.mutateAsync({
      releaseId: selectedItem.release_id,
      data: { notes: editNotes || undefined },
    })
    setSelectedItem({ ...selectedItem, notes: editNotes || null })
    setEditingNotes(false)
    showToast('Notes updated', 'success')
  }

  const handleSendAlerts = async () => {
    try {
      const result = await triggerAlertsMutation.mutateAsync()
      if (result.success) {
        showToast(
          result.sent === 0
            ? 'No releases below target price'
            : `Sent ${result.sent} alert${result.sent === 1 ? '' : 's'} via Telegram`,
          'success'
        )
      } else {
        showToast(result.error || 'Failed to send alerts', 'error')
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Failed to send alerts', 'error')
    }
  }

  const getSortIcon = (column: string) => getCaretSortIcon(filters.sort || '', filters.order || 'desc', column)

  if (error) {
    return (
      <div className={styles.error}>
        <i className="bi bi-exclamation-triangle"></i>
        <p>Failed to load tracking list</p>
      </div>
    )
  }

  const renderGridView = (items: TrackedRelease[]) => (
    <div className={styles.cardGrid}>
      {items.map((item) => (
        <div
          key={item.id}
          className={cn(styles.card, selectedItem?.id === item.id && styles.cardSelected)}
          onClick={() => handleSelect(item)}
        >
          <img src={item.cover_image} alt={item.title} className={styles.cardCover} />
          <div className={styles.cardBody}>
            <div className={styles.cardTitle}>{item.title}</div>
            <div className={styles.cardArtist}>{item.artist}</div>
            <div className={styles.cardMetric}>
              <span className={styles.cardMetricLabel}>Target</span>
              <span className={styles.cardMetricValue}>
                {item.target_buy_price ? formatCurrency(item.target_buy_price) : '-'}
              </span>
            </div>
            <div className={styles.cardMetric}>
              <span className={styles.cardMetricLabel}>Market</span>
              <span className={styles.cardMetricValue}>{formatCurrency(item.lowest_price)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  const renderCrateView = (items: TrackedRelease[]) => (
    <div className={styles.crateGrid}>
      {items.map((item) => (
        <div
          key={item.id}
          className={cn(styles.crateItem, selectedItem?.id === item.id && styles.crateSelected)}
          onClick={() => handleSelect(item)}
        >
          <img src={item.cover_image} alt={item.title} className={styles.crateThumb} />
          <div className={styles.crateInfo}>
            <div className={styles.crateTitle}>{item.title}</div>
            <div className={styles.crateArtist}>{item.artist}</div>
          </div>
          <div className={styles.crateMetrics}>
            <div className={styles.crateMetric}>
              <div className={styles.crateMetricLabel}>Target</div>
              <div className={styles.crateMetricValue}>
                {item.target_buy_price ? formatCurrency(item.target_buy_price) : '-'}
              </div>
            </div>
            <div className={styles.crateMetric}>
              <div className={styles.crateMetricLabel}>Market</div>
              <div className={styles.crateMetricValue}>{formatCurrency(item.lowest_price)}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <h1>Watchlist</h1>
        <Button
          variant="secondary"
          icon={`bi ${triggerAlertsMutation.isPending ? 'bi-hourglass-split' : 'bi-telegram'}`}
          onClick={handleSendAlerts}
          disabled={triggerAlertsMutation.isPending}
          title="Send Telegram alerts for below-target releases"
        >
          {triggerAlertsMutation.isPending ? 'Sending…' : 'Send Alerts'}
        </Button>
      </div>

      <PageTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        defaultLabel="All Tracked"
        onTabClick={handleTabClick}
        onCreateTab={handleCreateTab}
        onDeleteTab={handleDeleteTab}
      />

      {/* Search Toolbar */}
      <div className={styles.toolbar}>
        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={v => updateFilters({ q: v || undefined })}
          onClear={() => updateFilters({ q: undefined })}
          placeholder="Search tracked releases..."
        />
        <div className={styles.controlsTrailing}>
          <ViewSortControls />
        </div>
      </div>

      {/* Stats */}
      {data?.stats && (
        <div className={styles.stats}>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{data.stats.total_tracked}</span>
            <span className={styles.statLabel}>Tracked</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{data.stats.with_target_price}</span>
            <span className={styles.statLabel}>With Target</span>
          </div>
          <div className={styles.statItem}>
            <span className={cn(styles.statValue, styles.highlight)}>
              {data.stats.below_target}
            </span>
            <span className={styles.statLabel}>Below Target</span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={styles.content}>
        <div className={styles.tableContainer}>
          {isLoading ? (
            <LoadingState message="Loading tracking..." />
          ) : data?.items && data.items.length > 0 ? (
            viewMode === 'grid' ? (
              renderGridView(data.items)
            ) : viewMode === 'crate' ? (
              renderCrateView(data.items)
            ) : (
              <>
                <div className={styles.tableHeader}>
                  <div className={styles.colCover}>Cover</div>
                  <div className={styles.colInfo}>Release</div>
                  <div
                    className={cn(styles.colPrice, styles.sortable)}
                    onClick={() => handleSort('target_buy_price')}
                  >
                    Target {getSortIcon('target_buy_price')}
                  </div>
                  <div className={styles.colMarket}>Market</div>
                  <div
                    className={cn(styles.colDate, styles.sortable)}
                    onClick={() => handleSort('added_at')}
                  >
                    Added {getSortIcon('added_at')}
                  </div>
                  <div className={styles.colActions}>Actions</div>
                </div>

                <div className={styles.tableBody}>
                  {data.items.map((item) => (
                    <div
                      key={item.id}
                      className={cn(
                        styles.tableRow,
                        selectedItem?.id === item.id && styles.selected,
                        !!(item.target_buy_price && item.lowest_price && item.lowest_price <= item.target_buy_price) && styles.belowTarget
                      )}
                      onClick={() => handleSelect(item)}
                    >
                      <div className={styles.colCover}>
                        <img src={item.cover_image} alt={item.title} className={styles.coverImage} />
                      </div>
                      <div className={styles.colInfo}>
                        <div className={styles.title}>{item.title}</div>
                        <div className={styles.artist}>{item.artist}</div>
                        <div className={styles.meta}>
                          {item.label} {item.catno && `• ${item.catno}`}
                        </div>
                      </div>
                      <div className={styles.colPrice}>
                        <div className={styles.price}>
                          {item.target_buy_price ? formatCurrency(item.target_buy_price) : '-'}
                        </div>
                      </div>
                      <div className={styles.colMarket}>
                        <div className={styles.price}>{formatCurrency(item.lowest_price)}</div>
                        <div className={styles.subtext}>{item.num_for_sale} for sale</div>
                      </div>
                      <div className={styles.colDate}>{formatDate(item.added_at)}</div>
                      <div className={styles.colActions}>
                        <IconButton
                          variant="ghost"
                          icon="bi bi-x-circle"
                          aria-label="Remove from tracking"
                          title="Remove from tracking"
                          onClick={(e) => { e.stopPropagation(); handleUntrack(item.release_id) }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )
          ) : (
            <div className={styles.empty}>
              <i className="bi bi-bookmark"></i>
              <p>No tracked releases yet</p>
              <p className={styles.emptyHint}>
                Add releases to your watchlist from the Releases page
              </p>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedItem && (
          <div className={styles.detailPanel}>
            <div className={styles.detailHeader}>
              <img src={selectedItem.cover_image} alt={selectedItem.title} className={styles.detailImage} />
              <div className={styles.detailInfo}>
                <h3 className={styles.detailTitle}>{selectedItem.title}</h3>
                <p className={styles.detailArtist}>{selectedItem.artist}</p>
              </div>
              <IconButton variant="secondary" icon="bi bi-x-lg" aria-label="Close" onClick={() => setSelectedItem(null)} className={styles.closeButton} />
            </div>

            <div className={styles.detailContent}>
              <div className={styles.detailSection}>
                <h4>Target</h4>
                <div className={styles.detailRow}>
                  <span>Target Buy Price</span>
                  {editingTarget ? (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <input
                        type="number"
                        step="0.01"
                        value={editTarget}
                        onChange={(e) => setEditTarget(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTarget(); if (e.key === 'Escape') setEditingTarget(false) }}
                        autoFocus
                        style={{ width: '80px', padding: '2px 6px', fontSize: '0.875rem', background: 'var(--stonks-bg-secondary)', border: '1px solid var(--stonks-border)', borderRadius: '4px', color: 'inherit' }}
                      />
                      <IconButton variant="primary" size="sm" icon="bi bi-check-lg" aria-label="Save" title="Save" onClick={handleSaveTarget} />
                      <IconButton variant="secondary" size="sm" icon="bi bi-x-lg" aria-label="Cancel" title="Cancel" onClick={() => setEditingTarget(false)} />
                    </div>
                  ) : (
                    <span
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                      onClick={() => { setEditTarget(String(selectedItem.target_buy_price ?? '')); setEditingTarget(true) }}
                      title="Click to edit"
                    >
                      {selectedItem.target_buy_price
                        ? formatCurrency(selectedItem.target_buy_price)
                        : <span style={{ color: 'var(--stonks-text-muted)' }}>Not set</span>}
                      <i className="bi bi-pencil" style={{ fontSize: '0.75rem', color: 'var(--stonks-text-muted)' }}></i>
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4>Market</h4>
                <div className={styles.detailRow}>
                  <span>Lowest Price</span>
                  <span>{formatCurrency(selectedItem.lowest_price)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span>Fair Value</span>
                  <span>{formatCurrency(selectedItem.fair_value)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span>For Sale</span>
                  <span>{selectedItem.num_for_sale}</span>
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  Notes
                  {!editingNotes && (
                    <IconButton
                      variant="ghost"
                      size="sm"
                      icon="bi bi-pencil"
                      aria-label="Edit notes"
                      title="Edit notes"
                      onClick={() => { setEditNotes(selectedItem.notes || ''); setEditingNotes(true) }}
                    />
                  )}
                </h4>
                {editingNotes ? (
                  <div>
                    <textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      rows={3}
                      autoFocus
                      style={{ width: '100%', padding: '6px', fontSize: '0.875rem', background: 'var(--stonks-bg-secondary)', border: '1px solid var(--stonks-border)', borderRadius: '4px', color: 'inherit', resize: 'vertical', boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                      <Button variant="primary" size="sm" icon="bi bi-check-lg" onClick={handleSaveNotes}>Save</Button>
                      <Button variant="secondary" size="sm" icon="bi bi-x-lg" onClick={() => setEditingNotes(false)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <p
                    className={styles.notes}
                    style={{ color: selectedItem.notes ? undefined : 'var(--stonks-text-muted)', cursor: 'pointer' }}
                    onClick={() => { setEditNotes(selectedItem.notes || ''); setEditingNotes(true) }}
                  >
                    {selectedItem.notes || 'Add notes…'}
                  </p>
                )}
              </div>

              <div className={styles.detailActions}>
                <Button variant="secondary" block icon="bi bi-x-circle" onClick={() => handleUntrack(selectedItem.release_id)}>
                  Remove from watchlist
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && (
        <Pagination
          page={data.page}
          totalPages={data.total_pages}
          hasNext={data.page < data.total_pages}
          hasPrev={data.page > 1}
          onPageChange={(page) => updateFilters({ page })}
          perPage={filters.per_page}
          onPerPageChange={(per_page) => updateFilters({ per_page })}
        />
      )}
    </div>
  )
}
